import { differenceInDays, format } from 'date-fns';
import { RotateCcw } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { MatterSectionCard } from '@/components/matter/MatterSectionCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  EncounterSchema,
  MedicalProvider,
  ProviderStatus,
} from '@/services/types/treatment-types';

import {
  findTreatmentGaps,
  getAllEncountersSorted,
  isValidDate,
  parseDate,
} from '../../treatment-timeline/utils';
import { CheckInModal } from './CheckInModal';

interface TreatmentTimelineCardProps {
  providers: MedicalProvider[];
  isLoading?: boolean;
  incidentDate?: string | null;
}

interface ProviderTimelineData {
  id: string;
  name: string;
  specialty?: string;
  encounters: EncounterSchema[];
  firstDate: Date | null;
  lastDate: Date | null;
  isOngoing: boolean;
  /** Unique services/treatments received across all encounters */
  uniqueServices: string[];
}

interface Gap {
  start: Date;
  end: Date;
  days: number;
  afterProviderId: string;
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Calculate provider timeline data from raw provider list
 */
const useTimelineData = (
  providers: MedicalProvider[],
  incidentDate?: string | null,
) => {
  return useMemo(() => {
    const approvedProviders = providers.filter(
      (p) => p.status === ProviderStatus.APPROVED,
    );

    // Get all sorted encounters to determine timeline bounds
    const allEncounters = getAllEncountersSorted(approvedProviders);

    if (allEncounters.length === 0) {
      return {
        providerData: [],
        timelineStart: null,
        timelineEnd: null,
        timeMarkers: [],
        gridLines: [],
        gaps: [],
        incidentPosition: null,
        hasOngoingTreatment: false,
      };
    }

    // Calculate timeline bounds - autoscale to fit data tightly
    const firstEncounterDate = parseDate(allEncounters[0].date);
    const lastEncounterDate = parseDate(
      allEncounters[allEncounters.length - 1].date,
    );
    const today = new Date();

    // Use incident date as start if provided and valid
    let timelineStart = firstEncounterDate;
    if (incidentDate && isValidDate(incidentDate)) {
      const incident = parseDate(incidentDate);
      if (incident < firstEncounterDate) {
        timelineStart = incident;
      }
    }

    // Check if any provider is still actively treating (ongoing)
    // A provider is ongoing if their last visit was within 60 days
    const hasOngoingTreatment = approvedProviders.some((provider) => {
      const encounters = (provider.encounters || []).filter(
        (e) => e.date && isValidDate(e.date),
      );
      if (encounters.length === 0) return false;
      const lastVisit = parseDate(encounters[encounters.length - 1].date!);
      return differenceInDays(today, lastVisit) <= 60;
    });

    // End at "today/present" only if there's ongoing treatment
    // Otherwise, end at the last encounter date
    let timelineEnd: Date;
    if (hasOngoingTreatment) {
      // Extend to today for ongoing treatment
      timelineEnd = today;
    } else {
      // End at last encounter
      timelineEnd = lastEncounterDate;
    }

    // Add padding to timeline bounds (5% on each side) to prevent chip clipping
    const totalSpan = differenceInDays(timelineEnd, timelineStart);
    const paddingDays = Math.max(3, Math.ceil(totalSpan * 0.05)); // At least 3 days padding
    timelineStart = new Date(timelineStart);
    timelineStart.setDate(timelineStart.getDate() - paddingDays);
    if (!hasOngoingTreatment) {
      timelineEnd = new Date(timelineEnd);
      timelineEnd.setDate(timelineEnd.getDate() + paddingDays);
    }

    // Build provider timeline data
    const providerData: ProviderTimelineData[] = approvedProviders
      .map((provider) => {
        const encounters = (provider.encounters || [])
          .filter((e) => e.date && isValidDate(e.date))
          .sort(
            (a, b) =>
              parseDate(a.date!).getTime() - parseDate(b.date!).getTime(),
          );

        if (encounters.length === 0) {
          return null;
        }

        const firstDate = parseDate(encounters[0].date!);
        const lastDate = parseDate(encounters[encounters.length - 1].date!);

        // Determine if treatment is ongoing (last visit within 60 days)
        const daysSinceLastVisit = differenceInDays(today, lastDate);
        const isOngoing = daysSinceLastVisit <= 60;

        // Collect unique services across all encounters
        const allServices = encounters.flatMap((e) => e.services || []);
        const uniqueServices = [...new Set(allServices)].slice(0, 4); // Limit to 4 for display

        return {
          id: provider.id,
          name: provider.name || 'Unnamed Provider',
          specialty: provider.treatment || undefined,
          encounters,
          firstDate,
          lastDate,
          isOngoing,
          uniqueServices,
        };
      })
      .filter(Boolean) as ProviderTimelineData[];

    // Sort providers by first encounter date
    providerData.sort((a, b) => {
      if (!a.firstDate || !b.firstDate) return 0;
      return a.firstDate.getTime() - b.firstDate.getTime();
    });

    // Generate time markers and grid lines based on timeline length
    // Extend range beyond 0-100% to support panning (-50% to 150%)
    const totalDays = differenceInDays(timelineEnd, timelineStart);
    const timeMarkers: { label: string; position: number }[] = [];
    const gridLines: { position: number }[] = [];

    // Extended bounds for panning (50% extra on each side)
    const extendedStartDate = new Date(timelineStart);
    extendedStartDate.setDate(
      extendedStartDate.getDate() - Math.ceil(totalDays * 0.5),
    );
    const extendedEndDate = new Date(timelineEnd);
    extendedEndDate.setDate(
      extendedEndDate.getDate() + Math.ceil(totalDays * 0.5),
    );

    // Determine appropriate interval based on timeline length
    if (totalDays <= 14) {
      // Very short: show every day
      let currentDay = new Date(extendedStartDate);
      while (currentDay <= extendedEndDate) {
        const position =
          (differenceInDays(currentDay, timelineStart) / totalDays) * 100;
        timeMarkers.push({
          label: format(currentDay, 'd'),
          position,
        });
        gridLines.push({ position });
        currentDay = new Date(currentDay);
        currentDay.setDate(currentDay.getDate() + 1);
      }
    } else if (totalDays <= 45) {
      // Short: show every week
      let currentDay = new Date(extendedStartDate);
      // Align to start of week
      const dayOfWeek = currentDay.getDay();
      currentDay.setDate(currentDay.getDate() - dayOfWeek);

      while (currentDay <= extendedEndDate) {
        const position =
          (differenceInDays(currentDay, timelineStart) / totalDays) * 100;
        timeMarkers.push({
          label: format(currentDay, 'MMM d'),
          position,
        });
        gridLines.push({ position });
        currentDay = new Date(currentDay);
        currentDay.setDate(currentDay.getDate() + 7);
      }
    } else {
      // Longer timelines: show months
      let monthInterval = 1;
      if (totalDays > 365 * 2) monthInterval = 6;
      else if (totalDays > 365) monthInterval = 3;
      else if (totalDays > 180) monthInterval = 2;

      const startMonth = new Date(
        extendedStartDate.getFullYear(),
        extendedStartDate.getMonth(),
        1,
      );
      let currentMonth = new Date(startMonth);

      while (currentMonth <= extendedEndDate) {
        const position =
          (differenceInDays(currentMonth, timelineStart) / totalDays) * 100;
        timeMarkers.push({
          label: MONTH_LABELS[currentMonth.getMonth()],
          position,
        });
        gridLines.push({ position });
        currentMonth = new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth() + monthInterval,
          1,
        );
      }
    }

    // Find gaps in overall treatment (across all providers)
    const allGaps = findTreatmentGaps(allEncounters, 30);
    const gaps: Gap[] = allGaps.map((gap) => ({
      ...gap,
      afterProviderId: '', // We'll show gaps at the timeline level
    }));

    // Calculate incident date position
    let incidentPosition: number | null = null;
    if (incidentDate && isValidDate(incidentDate)) {
      const incident = parseDate(incidentDate);
      incidentPosition =
        (differenceInDays(incident, timelineStart) / totalDays) * 100;
    }

    return {
      providerData,
      timelineStart,
      timelineEnd,
      timeMarkers,
      gridLines,
      gaps,
      incidentPosition,
      hasOngoingTreatment,
    };
  }, [providers, incidentDate]);
};

/**
 * Calculate position percentage for a date
 */
const getDatePosition = (
  date: Date,
  timelineStart: Date,
  timelineEnd: Date,
): number => {
  const totalDays = differenceInDays(timelineEnd, timelineStart);
  if (totalDays === 0) return 0;
  const position = (differenceInDays(date, timelineStart) / totalDays) * 100;
  return Math.max(0, Math.min(100, position));
};

/**
 * Provider row in the timeline
 */
const ProviderLane = ({
  provider,
  timelineStart,
  timelineEnd,
  panOffset,
}: {
  provider: ProviderTimelineData;
  timelineStart: Date;
  timelineEnd: Date;
  panOffset: number;
}) => {
  return (
    <div className="flex items-center gap-6 py-1.5">
      {/* Provider info - fixed width */}
      <div className="w-44 flex-shrink-0">
        <div className="text-[13px] font-semibold text-zinc-900 truncate leading-tight">
          {provider.name}
        </div>
        <div className="text-[11px] text-zinc-500 leading-tight">
          {provider.specialty && (
            <span className="font-medium text-zinc-600">
              {provider.specialty} ·{' '}
            </span>
          )}
          {provider.encounters.length} encounter
          {provider.encounters.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Timeline bar */}
      <div className="flex-1 relative h-5 ml-4 overflow-hidden">
        {/* Individual encounter chips - with pan offset */}
        <div
          className="absolute inset-0"
          style={{ transform: `translateX(${panOffset}%)` }}
        >
          {provider.encounters.map((encounter, idx) => {
            if (!encounter.date) return null;
            const encounterDate = parseDate(encounter.date);
            const position = getDatePosition(
              encounterDate,
              timelineStart,
              timelineEnd,
            );

            // Get display label - primary service or truncated description
            const primaryService =
              encounter.services && encounter.services.length > 0
                ? encounter.services[0]
                : null;
            const displayLabel =
              primaryService || encounter.description || 'Visit';
            const truncatedLabel =
              displayLabel.length > 18
                ? `${displayLabel.slice(0, 16)}…`
                : displayLabel;

            return (
              <TooltipProvider key={encounter.id || idx}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 cursor-pointer transition-all hover:scale-105 hover:z-10"
                      style={{ left: `${position}%` }}
                    >
                      <div className="px-2 py-1 rounded-md text-[11px] font-medium bg-violet-600 text-white shadow-sm whitespace-nowrap max-w-[120px] truncate">
                        {truncatedLabel}
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs max-w-xs">
                    <div className="font-medium">
                      {format(encounterDate, 'MMM d, yyyy')}
                    </div>
                    {encounter.description && (
                      <div className="text-zinc-400 mt-1">
                        {encounter.description}
                      </div>
                    )}
                    {encounter.services && encounter.services.length > 0 && (
                      <div className="text-zinc-400 mt-1 pt-1 border-t border-zinc-700">
                        {encounter.services.join(', ')}
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const TreatmentTimelineCard = ({
  providers,
  isLoading,
  incidentDate,
}: TreatmentTimelineCardProps) => {
  const {
    providerData,
    timelineStart,
    timelineEnd,
    timeMarkers,
    gridLines,
    incidentPosition,
    hasOngoingTreatment,
  } = useTimelineData(providers, incidentDate);

  // Pan state
  const [panOffset, setPanOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; startOffset: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Check-in modal state
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);

  const isPanned = panOffset !== 0;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Only start drag on left click
      if (e.button !== 0) return;
      setIsDragging(true);
      dragStartRef.current = { x: e.clientX, startOffset: panOffset };
      e.preventDefault();
    },
    [panOffset],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !dragStartRef.current || !containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const deltaX = e.clientX - dragStartRef.current.x;
      // Convert pixel movement to percentage (relative to container width)
      const deltaPercent = (deltaX / containerWidth) * 100;
      setPanOffset(dragStartRef.current.startOffset + deltaPercent);
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      dragStartRef.current = null;
    }
  }, [isDragging]);

  const handleReset = useCallback(() => {
    setPanOffset(0);
  }, []);

  if (isLoading) {
    return (
      <MatterSectionCard title="Treatment Timeline">
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-6">
              <Skeleton className="h-8 w-44" />
              <Skeleton className="h-4 flex-1 ml-4" />
            </div>
          ))}
        </div>
      </MatterSectionCard>
    );
  }

  if (providerData.length === 0) {
    return (
      <MatterSectionCard title="Treatment Timeline">
        <div className="text-center py-8 text-sm text-zinc-600">
          No treatment data available yet
        </div>
      </MatterSectionCard>
    );
  }

  return (
    <>
      <CheckInModal
        open={checkInModalOpen}
        onOpenChange={setCheckInModalOpen}
      />
      <MatterSectionCard
        title="Treatment Timeline"
        headerActions={
          <div className="flex items-center gap-2">
            <div
              className={`transition-all duration-200 ease-out ${
                isPanned
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 translate-x-2 pointer-events-none'
              }`}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="h-6 px-1.5 text-[11px] text-zinc-500 hover:text-zinc-900"
              >
                <RotateCcw className="h-2 w-2" />
                Reset
              </Button>
            </div>
          </div>
        }
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          ref={containerRef}
          className="relative select-none"
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {/* Time markers header */}
          <div className="flex items-center gap-6 mb-2">
            <div className="w-44 flex-shrink-0" />
            <div className="flex-1 relative h-5 ml-4 overflow-hidden">
              <div
                className="absolute inset-0"
                style={{ transform: `translateX(${panOffset}%)` }}
              >
                {timeMarkers.map((marker, idx) => (
                  <span
                    key={idx}
                    className="absolute text-[11px] font-medium text-zinc-500 -translate-x-1/2"
                    style={{ left: `${marker.position}%` }}
                  >
                    {marker.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Timeline grid lines and content */}
          <div className="relative">
            {/* Vertical dashed grid lines */}
            <div className="flex items-center gap-6 absolute w-full h-full pointer-events-none">
              <div className="w-44 flex-shrink-0" />
              <div className="flex-1 relative h-full ml-4 overflow-hidden">
                <div
                  className="absolute inset-0"
                  style={{ transform: `translateX(${panOffset}%)` }}
                >
                  {gridLines.map((line, idx) => (
                    <div
                      key={idx}
                      className="absolute h-full border-l border-dashed border-zinc-200"
                      style={{ left: `${line.position}%` }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Incident date marker */}
            {incidentPosition !== null && timelineStart && (
              <div className="flex items-center gap-6 absolute w-full pointer-events-none z-10">
                <div className="w-44 flex-shrink-0" />
                <div className="flex-1 relative ml-4 overflow-hidden">
                  <div
                    className="absolute inset-0"
                    style={{ transform: `translateX(${panOffset}%)` }}
                  >
                    <div
                      className="absolute h-full w-px bg-zinc-900"
                      style={{ left: `${incidentPosition}%` }}
                    />
                    <div
                      className="absolute -bottom-6 text-[10px] font-semibold text-zinc-900 -translate-x-1/2 whitespace-nowrap"
                      style={{ left: `${incidentPosition}%` }}
                    >
                      {incidentDate &&
                        format(parseDate(incidentDate), 'MMM d, yyyy')}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Present marker - only show if there's ongoing treatment */}
            {hasOngoingTreatment && (
              <div className="flex items-center gap-6 absolute w-full pointer-events-none z-10 h-full">
                <div className="w-44 flex-shrink-0" />
                <div className="flex-1 relative h-full ml-4 overflow-hidden">
                  <div
                    className="absolute inset-0"
                    style={{ transform: `translateX(${panOffset}%)` }}
                  >
                    <div
                      className="absolute h-full w-px bg-zinc-300"
                      style={{ right: 0 }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Provider lanes */}
            <div className="divide-y divide-zinc-100">
              {providerData.map((provider) => (
                <ProviderLane
                  key={provider.id}
                  provider={provider}
                  timelineStart={timelineStart!}
                  timelineEnd={timelineEnd!}
                  panOffset={panOffset}
                />
              ))}
            </div>

            {/* Present label - only show if there's ongoing treatment */}
            {hasOngoingTreatment && (
              <div className="flex items-center gap-6 mt-1">
                <div className="w-44 flex-shrink-0" />
                <div className="flex-1 relative ml-4 overflow-hidden">
                  <div
                    className="absolute inset-0"
                    style={{ transform: `translateX(${panOffset}%)` }}
                  >
                    <span className="absolute right-0 text-[10px] font-semibold text-zinc-600 translate-x-1/2">
                      Present
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </MatterSectionCard>
    </>
  );
};

export default TreatmentTimelineCard;
